<script setup>
import { onMounted, ref, watch } from 'vue'

/**
 * Text typed in place on the editor's page (D-150): it takes the look of the document around
 * it (a heading, a caption, a table cell), so nothing about it looks like a form field. Plain
 * text; Enter makes a new line only where lines are allowed. A number shows formatted until
 * it is being typed — the text swapped for the typed one, the caret where the click was (or at
 * its end), never lost.
 */
const props = defineProps({
  value: { type: [String, Number, null], default: '' },
  readonly: Boolean,
  multiline: Boolean,
  placeholder: { type: String, default: '' },
  // For numbers: shown text when not typing; the typed text is read back with parse().
  display: { type: String, default: null },
  parse: { type: Function, default: null },
})
const emit = defineEmits(['update'])
const el = ref(null)
const focused = ref(false)

const shown = () => (focused.value || props.display === null ? String(props.value ?? '') : props.display)
function paint() {
  if (el.value && el.value.innerText !== shown()) el.value.innerText = shown()
}
onMounted(paint)
watch(() => [props.value, props.display], () => {
  if (!focused.value) paint()
})

function read() {
  const text = (el.value?.innerText ?? '').replace(/\u00a0/g, ' ')
  return props.multiline ? text.replace(/\n$/, '') : text.replace(/\n/g, ' ')
}
function input() {
  const text = read()
  emit('update', props.parse ? props.parse(text) : text)
}
// Where the press was: the caret goes there once the text is swapped for the typed one.
let pressedAt = null
function press(e) {
  pressedAt = { x: e.clientX, y: e.clientY }
}
function focus() {
  focused.value = true
  const before = el.value?.innerText
  paint()
  if (el.value && el.value.innerText !== before) placeCaret()
  pressedAt = null
}
function placeCaret() {
  const node = el.value
  const selection = window.getSelection()
  if (!node || !selection) return
  let range = pressedAt && document.caretRangeFromPoint ? document.caretRangeFromPoint(pressedAt.x, pressedAt.y) : null
  if (!range || !node.contains(range.startContainer)) {
    range = document.createRange()
    range.selectNodeContents(node)
    range.collapse(false)
  }
  selection.removeAllRanges()
  selection.addRange(range)
}
function blur() {
  focused.value = false
  paint()
}
function key(e) {
  if (e.key === 'Enter' && !props.multiline) {
    e.preventDefault()
    el.value?.blur()
  }
}
// Pasted text without its formatting.
function paste(e) {
  e.preventDefault()
  const text = e.clipboardData?.getData('text/plain') ?? ''
  document.execCommand('insertText', false, props.multiline ? text : text.replace(/\r?\n/g, ' '))
}
</script>

<template>
  <span
    ref="el"
    :class="['doc-edit', multiline ? 'doc-edit-lines' : '', readonly ? '' : 'doc-edit-on']"
    :contenteditable="readonly ? 'false' : 'true'"
    :data-placeholder="placeholder"
    spellcheck="true"
    @input="input"
    @mousedown="press"
    @focus="focus"
    @blur="blur"
    @keydown="key"
    @paste="paste"
  ></span>
</template>
